import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { HUB_SESSION_STORAGE_KEY, clearPersistedHubSession } from '../utils/session';

const safePersistStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
      const raw = window.localStorage.getItem(name);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('[STORE] Failed to hydrate persisted Hub state:', error);
      clearPersistedHubSession();
      return null;
    }
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(name, JSON.stringify(value));
    } catch (error) {
      console.warn('[STORE] Failed to persist Hub state:', error);
    }
  },
  removeItem: (name) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.removeItem(name);
    } catch (error) {
      console.warn('[STORE] Failed to remove persisted Hub state:', error);
    }
  },
};

export const useStore = create(
  persist(
    (set, get) => ({
      // Auth state
      user: null,
      token: null,
      
      // Login
      login: (user, token) => set({ user, token }),
      
      // Logout
      logout: () => {
        set({ user: null, token: null });
        clearPersistedHubSession();
      },
      
      // Check if logged in
      isLoggedIn: () => {
        const state = get();
        return !!state.token && !!state.user;
      },
      
      // Emma live-state for the top-bar orb: 'idle' | 'thinking' | 'paused'.
      // Controls' "Pause Emma" kill switch sets this to 'paused' so the orb
      // reflects the paused state app-wide (spec 05 §Controls). Persisted so a
      // pause survives reload; cleared back to 'idle' when resumed.
      emmaLiveState: 'idle',
      setEmmaLiveState: (emmaLiveState) => set({ emmaLiveState }),

      // Analytics date range — shared across EVERY analytics section so all
      // metrics reflect the SAME period (no mixing 30d/today). Defaults to
      // today. Not persisted → resets to today each session.
      // preset ∈ 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom'.
      analyticsRange: (() => {
        const t = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
        return { start: t, end: t, preset: 'today' };
      })(),
      setAnalyticsRange: (range) => set({ analyticsRange: { ...get().analyticsRange, ...range } }),

      // Dashboard stats
      stats: null,
      setStats: (stats) => set({ stats }),
      
      // Cases
      cases: [],
      setCases: (cases) => set({ cases }),
      
      // Alerts
      alerts: [],
      alertsCount: 0,
      setAlerts: (alerts, totalOpen) => set({ alerts, alertsCount: totalOpen ?? alerts.filter(a => a.status === 'open').length }),

      // Inbox (count and hasOld for sidebar badge)
      inboxCount: 0,
      inboxHasOld: false,
      setInbox: (data) => set({
        inboxCount: data?.count ?? 0,
        inboxHasOld: !!data?.hasOld,
      }),
      
      // Refund safeguard paused count (sidebar badge)
      refundsPausedCount: 0,
      setRefundsPausedCount: (count) => set({ refundsPausedCount: count }),

      // Returns
      returns: [],
      setReturns: (returns) => set({ returns }),
      
      // Order Changes
      orderChanges: [],
      setOrderChanges: (orderChanges) => set({ orderChanges }),
      
      // Knowledge Base
      faqs: [],
      sops: [],
      products: [],
      policies: [],
      setFaqs: (faqs) => set({ faqs }),
      setSops: (sops) => set({ sops }),
      setProducts: (products) => set({ products }),
      setPolicies: (policies) => set({ policies }),
      
      // Loading states
      isLoading: false,
      setLoading: (isLoading) => set({ isLoading }),
      
      // Error handling
      error: null,
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // View-as role (admin only): see Hub as another role. Not persisted.
      viewAsRole: null,
      setViewAsRole: (role) => set({ viewAsRole: role || null }),

      // Impersonation: admin logs in as another user with a real token swap
      viewAsUserId: null,
      viewAsUserContext: null,
      _adminUser: null,
      _adminToken: null,
      impersonateUser: (impersonatedUser, impersonatedToken, context) => {
        const state = get();
        set({
          _adminUser: state.user,
          _adminToken: state.token,
          user: impersonatedUser,
          token: impersonatedToken,
          viewAsUserId: impersonatedUser.id,
          viewAsUserContext: context || impersonatedUser,
          viewAsRole: null,
        });
      },
      exitImpersonation: () => {
        const state = get();
        if (state._adminToken && state._adminUser) {
          set({
            user: state._adminUser,
            token: state._adminToken,
            _adminUser: null,
            _adminToken: null,
            viewAsUserId: null,
            viewAsUserContext: null,
            viewAsRole: null,
          });
        } else {
          set({ viewAsUserId: null, viewAsUserContext: null, viewAsRole: null });
        }
      },
      setViewAsUser: (userId, context) => set({
        viewAsUserId: userId || null,
        viewAsUserContext: context || null,
        viewAsRole: null
      }),
      clearViewAsUser: () => {
        const state = get();
        if (state._adminToken && state._adminUser) {
          set({
            user: state._adminUser,
            token: state._adminToken,
            _adminUser: null,
            _adminToken: null,
            viewAsUserId: null,
            viewAsUserContext: null,
            viewAsRole: null,
          });
        } else {
          set({ viewAsUserId: null, viewAsUserContext: null });
        }
      },
    }),
    {
      name: HUB_SESSION_STORAGE_KEY,
      storage: safePersistStorage,
      partialize: (state) => ({ user: state.user, token: state.token, emmaLiveState: state.emmaLiveState }),
    }
  )
);
